import { forwardRef, useRef, useState, useImperativeHandle, useEffect } from 'react'
import styles from './LineButton.module.scss'
import Link from '@/components/Link/Link'
import classNames from 'classnames'
import gsap from 'gsap'
import useBreakpoint from '@/hooks/use-breakpoint'

const GSAP_CONFIG = {
  duration: 0.8,
  ease: 'Power4.easeOut',
}

const ANIMATING_OUT_DATASET = 'isAnimatingOut'
const ANIMATING_OUT_DATASET_VALUE = 'true'

const ANIMATION_STATE = {
  IDLE: 'idle',
  ANIMATED_OUT: 'animatingOut',
}

const LineButton = forwardRef(
  (
    {
      label,
      link,
      onMouseEnter = () => {},
      onMouseLeave = () => {},
      ariaLabel,
      onClick = () => {},
      theme = 'light',
      id = undefined,
      className = undefined,
      children,
      showInitially = true,
      hideLines = false,
      tabIndex = null,
    },
    ref,
  ) => {
    const [indexes, setIndexes] = useState([0])
    const indexesRef = useRef(indexes)
    const $inner = useRef()
    const animationStateRef = useRef(ANIMATION_STATE.IDLE)
    const resetDebounceTimeoutRef = useRef(null)
    const allowResetDebounceTimeoutRef = useRef(false)
    const [isHovering, setIsHovering] = useState(false)
    const [isCleanup, setIsCleanup] = useState(false)
    const { isMobile } = useBreakpoint()

    const getElementsByIndex = index => {
      if (!$inner.current) return { label: null, line: null }
      const label = $inner.current.querySelector(`[data-label-index="${index}"]`)
      const line = $inner.current.querySelector(`[data-line-index="${index}"]`)
      return {
        label: label,
        line: line,
      }
    }

    const increaseIndexes = () => {
      setIndexes(prev => {
        const lastIndex = prev[prev?.length - 1]
        const newIndexes = [...prev, lastIndex + 1]
        indexesRef.current = newIndexes
        return newIndexes
      })
    }

    const removePropsFromElements = elements => {
      elements.forEach(element => {
        if (!element) return
        gsap.killTweensOf(element)
        gsap.set(element, {
          clearProps: true,
        })
      })
    }

    const reset = (removeProps = true) => {
      allowResetDebounceTimeoutRef.current = false
      setIndexes(prev => {
        const lastIndex = prev[prev?.length - 1]
        const newIndexes = [lastIndex]
        indexesRef.current = newIndexes
        return newIndexes
      })

      const { label, line } = getElementsByIndex(indexesRef.current[indexesRef.current?.length - 1])

      if (removeProps) {
        removePropsFromElements([label, line])
      } else {
        if (label) {
          gsap.set(label, {
            y: 0,
            autoAlpha: 1,
            opacity: 1,
          })
        }
        if (line) {
          gsap.set(line, {
            x: 0,
            scaleX: 1,
            autoAlpha: 1,
            opacity: 1,
          })
        }
      }

      if (label) {
        label.dataset[ANIMATING_OUT_DATASET] = 'false'
      }
      if (line) {
        line.dataset[ANIMATING_OUT_DATASET] = 'false'
      }
    }

    const animations = {
      label: {
        in: element => {
          animationStateRef.current = ANIMATION_STATE.IDLE
          if (!element) {
            return console.warn('No element exists in animateLabelIn')
          }
          gsap.killTweensOf(element)
          gsap.to(element, {
            duration: 0.0001,
            autoAlpha: 1,
            delay: 0.05,
          })
          gsap.fromTo(
            element,
            {
              y: '120%',
            },
            {
              ...GSAP_CONFIG,
              y: 0,
            },
          )
        },
        out: (element, callback) => {
          if (!element) {
            return console.warn('No element exists in animateLabelIn')
          }
          element.dataset[ANIMATING_OUT_DATASET] = ANIMATING_OUT_DATASET_VALUE
          gsap.killTweensOf(element)
          gsap.to(element, {
            ...GSAP_CONFIG,
            y: '-120%',
            onComplete: () => {
              if (callback) callback()
            },
          })
        },
      },
      line: {
        in: element => {
          if (hideLines) return
          if (!element) {
            return console.warn('No element exists in animateLabelIn')
          }
          gsap.killTweensOf(element)
          gsap.to(element, {
            duration: 0.0001,
            autoAlpha: 1,
            delay: 0.05,
          })
          gsap.fromTo(
            element,
            {
              x: '-105%',
              scaleX: 0,
            },
            {
              ...GSAP_CONFIG,
              x: 0,
              scaleX: 1,
              delay: 0.075,
            },
          )
        },
        out: (element, callback) => {
          if (hideLines) return
          if (!element) {
            return console.warn('No element exists in animateLabelIn')
          }
          element.dataset[ANIMATING_OUT_DATASET] = ANIMATING_OUT_DATASET_VALUE
          gsap.killTweensOf(element)
          gsap.to(element, {
            ...GSAP_CONFIG,
            x: '105%',
            onComplete: () => {
              if (callback) callback()
            },
          })
        },
      },
    }

    const animateOutOldIndexes = () => {
      const oldIndexes = indexesRef.current.filter((_, i) => {
        if (indexesRef?.current?.length === 1) return true
        return i !== indexesRef.current.length - 1
      })

      const elements = []
      oldIndexes.forEach(index => {
        elements.push(getElementsByIndex(index))
      })

      elements.forEach(elementGroup => {
        const { label, line } = elementGroup
        if (label?.dataset[ANIMATING_OUT_DATASET] !== ANIMATING_OUT_DATASET_VALUE) {
          animations.label.out(label)
        }
        if (line?.dataset[ANIMATING_OUT_DATASET] !== ANIMATING_OUT_DATASET_VALUE) {
          animations.line.out(line)
        }
      })
    }

    const animateInNewIndex = () => {
      const newIndex = indexesRef.current[indexesRef.current?.length - 1]
      const { line, label } = getElementsByIndex(newIndex)

      animations.label.in(label)
      animations.line.in(line)
    }

    const rotateText = () => {
      if (animationStateRef.current === ANIMATION_STATE.ANIMATED_OUT) return
      increaseIndexes()
      setTimeout(() => {
        animateInNewIndex()
        animateOutOldIndexes()
      }, 2)
    }

    useImperativeHandle(ref, () => ({
      reset: () => {
        reset()
      },
      animateIn: () => {
        const lastIndex = indexesRef.current[indexesRef.current?.length - 1]

        if (typeof lastIndex !== 'number') {
          return
        }

        const { label, line } = getElementsByIndex(lastIndex)

        removePropsFromElements([label, line])

        animations.label.in(label)
        animations.line.in(line)
      },
      animateOut: () => {
        allowResetDebounceTimeoutRef.current = false
        if (animationStateRef.current === ANIMATION_STATE.ANIMATED_OUT) {
          return
        }
        animationStateRef.current = ANIMATION_STATE.ANIMATED_OUT
        const elements = []
        indexesRef.current.forEach(index => {
          elements.push(getElementsByIndex(index))
        })
        elements.forEach((elementGroup, i) => {
          const { label, line } = elementGroup
          if (label?.dataset[ANIMATING_OUT_DATASET] !== ANIMATING_OUT_DATASET_VALUE) {
            animations.label.out(label)
          }
          if (line?.dataset[ANIMATING_OUT_DATASET] !== ANIMATING_OUT_DATASET_VALUE) {
            animations.line.out(line, () => {
              if (i === elements.length - 1) {
                reset()
              }
            })
          }
        })
      },
    }))

    const handleMouseEnter = () => {
      if (isMobile) return
      allowResetDebounceTimeoutRef.current = true
      setIsHovering(true)
      rotateText()

      if (onMouseEnter) {
        onMouseEnter()
      }
    }

    const handleMouseLeave = () => {
      if (isMobile) return
      setIsHovering(false)
      // rotateText()

      if (onMouseLeave) {
        onMouseLeave()
      }
    }

    const handleClick = () => {
      if (onClick) {
        onClick()
      }
    }

    useEffect(() => {
      if (resetDebounceTimeoutRef.current) {
        clearTimeout(resetDebounceTimeoutRef.current)
      }

      if (indexes.length === 1 || isHovering) {
        return
      }

      if (!allowResetDebounceTimeoutRef.current) {
        return
      }

      resetDebounceTimeoutRef.current = setTimeout(
        () => {
          setIsCleanup(true)
          reset(false)
          setTimeout(() => {
            setIsCleanup(false)
          }, 10)
        },
        GSAP_CONFIG.duration * 1000 * 1.02,
      )
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [indexes, isHovering])

    const content = (
      <div
        className={styles.inner}
        ref={$inner}
      >
        <span
          className={styles.labelHidden}
          data-label-text
        >
          {label}
        </span>
        {indexes.map(index => {
          return (
            <span
              key={`${index}-label`}
              className={styles.label}
              data-label-index={index}
              data-label-text
            >
              {label && !children && label}
              {children && children}
            </span>
          )
        })}
        {indexes.map(index => {
          return (
            <span
              className={styles.line}
              data-line-index={index}
              key={`${index}-line-element`}
            />
          )
        })}
      </div>
    )

    const classes = classNames(
      styles.LineButton,
      className,
      { [styles[theme]]: theme },
      { [styles.showInitially]: showInitially },
      { [styles.hideLines]: hideLines },
      { [styles.isCleanup]: isCleanup },
    )

    if (link) {
      return (
        <Link
          link={link}
          className={classes}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onClick={handleClick}
          aria-label={ariaLabel}
        >
          {content}
        </Link>
      )
    }

    return (
      <button
        className={classes}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        aria-label={ariaLabel}
        type="button"
        id={id}
        {...(typeof tabIndex === 'number' ? { tabIndex } : {})}
      >
        {content}
      </button>
    )
  },
)

LineButton.displayName = 'LineButton'

export default LineButton
